import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CreateCasePopupComponent } from 'app/shared/components/create-case-popup/create-case-popup.component';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import {
  DataTableColumnFilters,
  DataTableColumnFiltersApi,
  DataTableColumnModel,
  DataTableConfigSettings,
  DataTableStateModel
} from 'app/shared/components/dynamic-table/dynamic-table.model';
import { PaginationComponent } from 'app/shared/components/pagination/pagination.component';
import { TitleHeaderComponent } from 'app/shared/components/title-header/title-header.component';
import { KnowledgeLibraryService } from './knowledge-library.service';
import { KnowledgeLibraryItem } from './knowledge-library.model';
import { KNOWLEDGE_LIBRARY_TABLE_CONFIG, KNOWLEDGE_LIBRARY_TABLE_STATE } from './knowledge-library-table.config';
import { finalize, take } from 'rxjs';
import { Router } from '@angular/router';
import { GlobalSearchComponent } from 'app/shared/components/global-search/global-search.component';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { HelperService } from 'app/shared/services/helper.service';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';

@Component({
  selector: 'app-knowledge-library',
  standalone: true,
  imports: [TitleHeaderComponent, CommonModule, TranslateModule, CreateCasePopupComponent, DynamicTableComponent, PaginationComponent, GlobalSearchComponent],
  templateUrl: './knowledge-library.component.html',
  styleUrl: './knowledge-library.component.scss'
})
export class KnowledgeLibraryComponent {
  filters: DataTableColumnFiltersApi[] = [];
  cases: KnowledgeLibraryItem[] = [];
  columns = this.helperService.deepCopy(KNOWLEDGE_LIBRARY_TABLE_CONFIG);
  totalCount = 0;
  error: string | null = null;
  casesDatatableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(KNOWLEDGE_LIBRARY_TABLE_STATE.gridName);
  casesDataTableState: DataTableStateModel = this.casesDatatableStateFromStorage ? this.casesDatatableStateFromStorage : { ...KNOWLEDGE_LIBRARY_TABLE_STATE };
  defaultColumns = this.helperService.deepCopy(KNOWLEDGE_LIBRARY_TABLE_CONFIG);
  selectedFilterName = '';
  isDataTableReady: boolean = false;
  datatableDisplayAllNames: DataTableColumnFilters[] = [];

  constructor(
    private knowledgeLibraryService: KnowledgeLibraryService,
    private router: Router,
    private configurationService: ConfigurationService,
    private helperService: HelperService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.getDataTableConfiguration();
  }

  stripHtmlTags(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  setData() {
    this.setDataTableColumnFilters();
    this.datatableDisplayAllNames = [
      ...this.configurationService.getStatusItems(),
      ...this.configurationService.getNextActionStatusItemsForFilter(),
      ...this.configurationService.getCaseStageItems(),
      ...this.configurationService.getBooleanDataTableColumnFilters(),
      ...this.configurationService.getSeverityItems(),
    ];

    this.getKnowledgelibraryList();
  }

  getKnowledgelibraryList(): void {
    this.error = null;

    this.knowledgeLibraryService
      .getElkKnowledgelibraryList(this.casesDataTableState)
      .pipe(
        take(1),
        finalize(() => {
          this.isDataTableReady = true;
        })
      )
      .subscribe((res) => {
        this.cases = res.items.map((item: any) => ({
          ...item,
          problemDescription: item.problemDescription ? this.stripHtmlTags(item.problemDescription) : '',
          msDynamicsReferenceNumber: item.msDynamicsReferenceNumber ? this.stripHtmlTags(item.msDynamicsReferenceNumber) : '',
          externalCellClass: this.helperService.getColorOfNextActionDueDate(item.nextActionDueDate)
        }));
        this.totalCount = res.totalCount;
      });
  }

  setDataTableColumnFilters() {
    this.columns.forEach((column: any) => {
      switch (column.prop) {
        case 'caseStatus':
          column.dataTableColumnFilters = this.configurationService.getStatusItems();
          break;
        case 'nextActionStatus':
          column.dataTableColumnFilters = this.configurationService.getNextActionStatusItemsForFilter();
          break;
        case 'caseStage':
          column.dataTableColumnFilters = this.configurationService.getCaseStageItems();
          break;
        case 'severity':
          column.dataTableColumnFilters = this.configurationService.getSeverityItems();
          break;
      }
    });

    this.defaultColumns.forEach((column: any) => {
      switch (column.prop) {
        case 'caseStatus':
          column.dataTableColumnFilters = this.configurationService.getStatusItems();
          break;
        case 'nextActionStatus':
          column.dataTableColumnFilters = this.configurationService.getNextActionStatusItemsForFilter();
          break;
        case 'caseStage':
          column.dataTableColumnFilters = this.configurationService.getCaseStageItems();
          break;
        case 'severity':
          column.dataTableColumnFilters = this.configurationService.getSeverityItems();
          break;
      }
    });
  }

  getDataTableConfiguration() {
    this.configurationService.getInitDataTableConfiguration(this.casesDataTableState.gridName)
      .pipe(
        take(1),
        finalize(() => {
          this.setData();
        })
      )
      .subscribe((res: DataTableConfigSettings) => {
        if (this.casesDatatableStateFromStorage) {
          this.columns = this.casesDataTableState.datatableColumns;
          this.filters = this.casesDataTableState.datatableFilters ?? [];
        } else {
          if (res && res.gridColumnSettings && !!JSON.parse(res.gridColumnSettings).length) {
            this.columns = JSON.parse(res.gridColumnSettings);
            this.casesDataTableState.datatableColumns = JSON.parse(res.gridColumnSettings);
          }

          if (res && res.gridFilterSettings && !!JSON.parse(res.gridFilterSettings).length) {
            this.filters = JSON.parse(res.gridFilterSettings);
            this.casesDataTableState.datatableFilters = JSON.parse(res.gridFilterSettings);
          }
        }

        if (res && res.name) {
          this.selectedFilterName = res.name;
        }

      });
  }

  onSaveDatatableSettings(data: DataTableStateModel) {
    const dataForSave = this.helperService.deepCopy(data);

    if (dataForSave && dataForSave.datatableColumns) {
      dataForSave.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.casesDataTableState.gridName,
      name: dataForSave.datatableFilterName || '',
      isGlobal: dataForSave.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForSave.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForSave.datatableColumns) || ''
    };

    this.configurationService.setDataTableConfiguration(params)
      .pipe(take(1))
      .subscribe(() => {
      });
  }

  onUpdateDatatableSettings(data: DataTableStateModel) {
    const dataForUpdate = this.helperService.deepCopy(data);

    if (dataForUpdate && dataForUpdate.datatableColumns) {
      dataForUpdate.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.casesDataTableState.gridName,
      name: dataForUpdate.datatableFilterName || '',
      isGlobal: dataForUpdate.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForUpdate.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForUpdate.datatableColumns) || ''
    };

    const filterId = dataForUpdate.datatableFilterId;

    this.configurationService.updateSavedFilter(filterId, params)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.show(this.translate.instant('messages.filter_successfully_updated'), ToastTypesEnum.SUCCESS);
        },
        error: (err) => {
          this.toastr.show(this.translate.instant('messages.failed_to_update_filter'), ToastTypesEnum.ERROR);
          console.error(err);
        }
      });
  }

  getDataTableState(event: DataTableStateModel): void {
    this.casesDataTableState = event;
    this.getKnowledgelibraryList();
  }

  getLinkedValue(data: any) {
    switch (data.column) {
      case 'caseIdentifier':
        this.router.navigate(['case-form/' + data.row.id]);
        break;
      case 'problemRecord':
        this.router.navigate(['problem-record/' + data.row.problemRecordId]);
        break;
    }
  }

  openCreateCasePopup() {
    this.helperService.createCasePopoupVisible = true;
    this.helperService.isSidebar = false;
  }

}
